import {DataTableSortStatus} from 'mantine-datatable';
import {
    sortRecords,
    useTableSort,
} from '@flexinet/ui-components';

import {
    useDeleteSubscriptionItemsMutation,
    useSubscriptionItemsInfiniteQuery,
} from './useSubscriptionItemsQuery';
import {useSubscriptionItemsSearch} from './useSubscriptionItemsSearch';
import {useSubscriptionItemsTableColumns} from './useSubscriptionItemsTableColumns';
import {SubscriptionItemsFilters} from './useSubscriptionItemsFilter';
import {SubscriptionItem} from '../types';

/**
 * Default sort: NEWEST FIRST, on `createdAtMillis`.
 *
 * The key must be immutable and non-null. `created: Int!` is set by Stripe when
 * the item is added to the subscription and never moves, so it satisfies both —
 * unlike `updated`, which does not exist on this schema at all (see types.ts).
 */
export const DEFAULT_SORT_STATUS: DataTableSortStatus<SubscriptionItem> = {
    columnAccessor: 'createdAtMillis',
    direction: 'desc',
};

/**
 * Which fields a keyword matches against. `id` is included because a Stripe
 * subscription-item id (`si_...`) is what an operator pastes in from the Stripe
 * dashboard.
 */
const matchesKeyword = (item: SubscriptionItem, needle: string): boolean => {
    if (!needle) {
        return true;
    }
    return [
        item.id,
        item.price,
        item.subscription,
    ].some(value => typeof value === 'string'
        && value.toLowerCase()
                .includes(needle));
};

/**
 * Whether a record satisfies every active filter. Filters are exact matches on
 * a stringified field; an empty value means the field is unset and is ignored,
 * so a cleared drawer field does not exclude everything.
 */
const matchesFilters = (
    item: SubscriptionItem,
    filters: SubscriptionItemsFilters,
): boolean =>
    Object.entries(filters)
          .every(([key, value]) => {
              if (value === '' || typeof value === 'undefined') {
                  return true;
              }
              const actual = item[key as keyof SubscriptionItem];
              return String(actual) === value;
          });

/**
 * FNP-918: the high-level list hook — query + search + filter + sort.
 *
 * ⚠️ SCOPE OF SORT AND FILTER: the pages FETCHED SO FAR.
 *
 * `SubscriptionItemsFilterInput` declares only `subscription: String!`, so the
 * server narrows to one subscription and nothing more — no keyword, no field
 * filter, no sort argument. Everything below therefore operates on the loaded
 * window only. Sorting page 1 does not sort the dataset, and a keyword matches
 * only rows the browser has already received.
 *
 * This is a property of the token-pagination model against this API, not a bug
 * and not an oversight. It is also why the footer states a LOADED count and
 * never a total.
 *
 * ⚠️ The parameter is the SUBSCRIPTION id. It was called `customerId`
 * throughout this module while its only caller passed a subscription id — see
 * SubscriptionItemsService for the filter bug that came with the confusion.
 */
export const useSubscriptionItems = (
    subscriptionId: string,
    filters: SubscriptionItemsFilters = {},
) => {
    const query = useSubscriptionItemsInfiniteQuery(subscriptionId);
    const {columns} = useSubscriptionItemsTableColumns();
    const {keyword} = useSubscriptionItemsSearch();
    const deleteMutation = useDeleteSubscriptionItemsMutation();

    // Pages are concatenated as they arrive. The type guard matters: a page can
    // be undefined mid-fetch, and a bare flatMap would throw (§9).
    const items: SubscriptionItem[] = (query.data?.pages ?? [])
        .flatMap(page => (page && 'items' in page ? page.items : []) ?? [])
        .filter(Boolean)
        // Normalise Stripe's epoch-seconds into millis ONCE, here, so the
        // Created column can both render and sort the same value.
        .map(item => ({
            ...item,
            createdAtMillis: typeof item.created === 'number'
                ? item.created * 1000
                : undefined,
        }));

    const needle = keyword.trim()
                          .toLowerCase();

    // Keyword and filters COMPOSE — both are applied, so using them together
    // narrows rather than resets (§7).
    const matched = items.filter(item =>
        matchesKeyword(item, needle) && matchesFilters(item, filters));

    // Sort state is COMPONENT-level (inside this hook's instance), not the
    // module-scoped store: sort is how ONE operator is viewing the table right
    // now, unlike keyword and filters which are shared across siblings (§3a).
    //
    // `useTableSort` is the SHARED comparator (FNP-912) — nulls last in both
    // directions, numbers numerically, strings via localeCompare.
    const {
        sortStatus,
        setSortStatus,
    } = useTableSort<SubscriptionItem>(matched, DEFAULT_SORT_STATUS);

    // Sorted separately rather than taking `sorted` from the hook: `matched` is
    // rebuilt every render (new object identities from the map above), and
    // sortRecords is the same comparator applied to the current array.
    const visibleItems = sortRecords(matched, sortStatus);

    return {
        /** The full loaded window BEFORE keyword/filter — the footer's denominator. */
        items,
        /** What the table renders: filtered then sorted. */
        visibleItems,
        columns,
        sortStatus,
        setSortStatus,
        keyword,
        loading: query.isLoading,
        isError: query.isError,
        error: query.error,
        isFetchingMore: query.isFetchingNextPage,
        // The control needs a token-ish value to decide "more available". The
        // query owns the real token; `hasNextPage` is the honest projection of
        // it, and the sentinel is never parsed or sent anywhere.
        nextToken: query.hasNextPage ? 'has-more' : null,
        fetchRecords: () => {
            // hasNextPage is checked BEFORE fetchNextPage (§9).
            if (query.hasNextPage && !query.isFetchingNextPage) {
                query.fetchNextPage();
            }
        },
        reload: () => query.refetch(),
        deleteRecords: (ids?: string[]) => {
            if (ids && ids.length > 0) {
                deleteMutation.mutate(ids);
            }
        },
        deleteMutation,
    };
};
